import { Formik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import * as yup from 'yup';
import Button from '../../../../components/Button/Button';
import Form from '../../../../components/form/Form';
import FormField from '../../../../components/form/FormField';
import { roundedWeightInLbs } from '../../../../lib/petUtils';
import { lbsToKg } from '../../../../lib/utils';
import { gqlTypes } from '../../../../types/gqlTypes';
import BreedSelect from '@/appview/RegisterChipV2/PetInfoStepV2/BreedSelect';
import GenderSelect from '@/appview/RegisterChipV2/PetInfoStepV2/GenderSelect';
import BirthdaySelect, { START_YEAR } from '@/appview/RegisterChipV2/PetInfoStepV2/BirthdaySelect';

export interface PetInfo {
  name?: string | null;
  species?: gqlTypes.Species | null;
  gender?: gqlTypes.Gender | null;
  breedId?: string | null;
  isMixedBreed?: boolean;
  color?: string | null;
  birthMonth?: number | null;
  birthDay?: number | null;
  birthYear?: number | null;
  weight?: string | null;
  householdId?: string | null;
  additionalInfo?: string | null;
}

const NUMBER = /^\d*$/;

const validationSchema = yup.object({
  name: yup.string().nullable().required(`Please enter the pet's name`),
  gender: yup.mixed<gqlTypes.Gender>().required(`Please select a gender`),
  weight: yup
    .string()
    .nullable()
    .test('is_valid_weight', `Please enter a valid weight`, (value) => {
      if (!value || value === '') return true;
      return NUMBER.test(value) && Number(value) >= 0;
    }),
  birthMonth: yup.number().nullable().required(`Please select a birth month`),
  birthYear: yup
    .number()
    .nullable()
    .required(`Please select a birth year`)
    .min(START_YEAR, `Birth year must be ${START_YEAR} or later`)
    .test('valid_date', `Birth date cannot be in the future`, function (value) {
      const { birthMonth, birthDay } = this.parent;
      if (!value || !birthMonth) return true;

      const now = DateTime.utc();
      const birthDate = DateTime.fromObject({
        year: value,
        month: birthMonth,
        day: birthDay ?? 1,
      });

      return birthDate <= now;
    }),
  birthDay: yup.number().nullable(), // Day is optional
});

export const petFormValuesFromPet = (pet: gqlTypes.basePet): PetInfo => ({
  gender: pet.gender,
  name: pet.name,
  weight: pet.weight > 0 ? roundedWeightInLbs(pet.weight).toString() : '',
  birthMonth: pet.monthOfBirth ?? null,
  birthDay: pet.dayOfBirth ?? null,
  birthYear: pet.yearOfBirth ?? null,
  breedId: pet.breed?.id ?? '',
  isMixedBreed: !pet.isPurebred,
  color: pet.color,
  species: pet.species || gqlTypes.Species.DOG,
  additionalInfo: pet.additionalInfo,
});

interface FormListenerProps {
  onValuesChanged?(values: PetInfo): void;
  onIsValidChanged?(isValid: boolean): void;
}

function FormListener({ onIsValidChanged, onValuesChanged }: FormListenerProps) {
  const { values: valuesWithActionTag, isValid } = useFormikContext<PetInfo & { actionTag?: string }>();

  useEffect(() => {
    if (onValuesChanged) {
      const { actionTag: _ignored, ...values } = valuesWithActionTag;
      onValuesChanged(values);
    }
  }, [valuesWithActionTag, onValuesChanged]);

  useEffect(() => {
    if (onIsValidChanged) {
      onIsValidChanged(isValid);
    }
  }, [isValid, onIsValidChanged]);

  return <React.Fragment />;
}

export interface CreateOrUpdatePetInput {
  name: string;
  gender: gqlTypes.Gender;
  species: gqlTypes.Species;
  breedId?: string | null;
  isPurebred: boolean;
  color?: string | null;
  yearOfBirth: number;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
  weight: number;
  householdId?: string | null;
  additionalInfo?: string | null;
}

interface ContactInfoFormProps extends FormListenerProps {
  initialValues?: PetInfo;
  onSubmit?: (values: CreateOrUpdatePetInput, errorMessage?: string) => void;
  onSkip?: () => void;
  onSecondaryAction?: (values: CreateOrUpdatePetInput) => void;
  secondaryActionLabel?: string;
  responseError?: string;
  disabled?: boolean;
  submitLabel?: string;
}

function transformFormValues(values: PetInfo, initialValues?: PetInfo): CreateOrUpdatePetInput | null {
  if (!values.name || !values.gender || !values.birthMonth || !values.birthYear) {
    return null;
  }

  return {
    name: values.name.trim(),
    gender: values.gender,
    species:
      initialValues?.species === gqlTypes.Species.CAT && values.species === gqlTypes.Species.OTHER
        ? gqlTypes.Species.CAT // Do not override a cat with an "other" species
        : values.species || gqlTypes.Species.DOG,
    breedId: values.breedId || undefined,
    isPurebred: values.breedId ? !values.isMixedBreed : false,
    color: values.color?.trim(),
    yearOfBirth: values.birthYear,
    monthOfBirth: values.birthMonth,
    dayOfBirth: values.birthDay,
    weight: values.weight ? lbsToKg(Number(values.weight)) : 0,
    additionalInfo: values.additionalInfo,
  };
}

export default function PetInfoForm({
  initialValues,
  responseError,
  onSubmit,
  onSkip,
  onSecondaryAction,
  secondaryActionLabel = 'Save and close',
  onValuesChanged,
  onIsValidChanged,
  disabled,
  submitLabel = 'Continue',
}: ContactInfoFormProps) {
  return (
    <Formik<PetInfo>
      initialValues={
        initialValues ?? {
          name: '',
          gender: null,
          breedId: null,
          color: null,
          birthMonth: null,
          birthDay: null,
          birthYear: null,
          weight: '',
          species: gqlTypes.Species.DOG,
          additionalInfo: '',
        }
      }
      onSubmit={(values: PetInfo) => {
        if (!onSubmit) {
          return;
        }

        const transformedValues = transformFormValues(values, initialValues);
        if (!transformedValues) {
          return;
        }

        onSubmit(transformedValues);
      }}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, submitForm, validateForm, values }) => {
        const isFormEmpty =
          !values.name &&
          !values.breedId &&
          !values.birthMonth &&
          !values.birthYear &&
          !values.gender &&
          !values.weight;

        const handleSecondaryClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();

          if (!onSecondaryAction) {
            return;
          }

          const errors = await validateForm();
          if (Object.keys(errors).length > 0) {
            return;
          }

          const transformedValues = transformFormValues(values, initialValues);
          if (!transformedValues) {
            return;
          }

          onSecondaryAction(transformedValues);
        };

        return (
          <div className="register-chip-next">
            <Form next onSubmit={handleSubmit}>
              <FormListener onIsValidChanged={onIsValidChanged} onValuesChanged={onValuesChanged} />
              <div className="form-group">
                <FormField type="text" name="name" placeholder="Name" label="Name" />
              </div>
              <GenderSelect name="gender" />
              <div className="form-group">
                <FormField type="text" name="color" placeholder="Color" label="Color (optional)" />
              </div>
              <BreedSelect name="breedId" isMixedBreed={!!values.isMixedBreed} isInline={true} />
              <BirthdaySelect
                monthFieldName="birthMonth"
                dayFieldName="birthDay"
                yearFieldName="birthYear"
                errorFieldName="birthYear"
                label="Birth Date"
              />
              <div className="form-group">
                <FormField type="text" name="weight" placeholder="Weight (lbs)" label="Weight (lbs, optional)" />
              </div>
              <div className="form-group">
                <FormField
                  type="text"
                  name="additionalInfo"
                  placeholder="Additional info"
                  label="Additional Info (optional)"
                />
              </div>
              <div className="form-group form-group--action form-group--multistep-action">
                {responseError && <div className="form-field__error">{responseError}</div>}
                {onSubmit && (
                  <Button
                    primary
                    label={submitLabel}
                    type="submit"
                    onClick={(e) => {
                      e.preventDefault();

                      // We want to let the user just bypass this form completely, so if they haven't filled
                      // anything out yet we'll skip. Otherwise we'll validate & submit.
                      if (onSkip && isFormEmpty) {
                        onSkip();
                        return;
                      }

                      submitForm();
                    }}
                    disabled={disabled}
                  />
                )}
                {onSecondaryAction && (
                  <Button label={secondaryActionLabel} onClick={handleSecondaryClick} disabled={disabled} />
                )}
              </div>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
}
